/* Grubwick backend client for menu extraction.
 *
 * POSTs a menu image OR PDF (as a data URL) to /gw/upload_menu (via gwFetch, which
 * attaches the Supabase token — this is a protected write) and maps the raw response
 * (src/api/types.ts) onto the UI's MenuItemRecord type. */
import { gwFetch } from "./client";
import type { MenuItemRecord } from "../data/adminData";
import type {
  ApiExtractedItem,
  UploadImageRequest,
  UploadImageResponse,
} from "./types";

/* UI-facing result of an extraction: a flat list of menu items (section name
   carried on `category`, ready to feed the editor later) plus the raw transcript
   the vision model read off the image. */
export interface ExtractedMenu {
  items: MenuItemRecord[];
  transcript: string;
}

/* The extractor labels an un-sectioned menu with the generic section name
   "Menu", which carries no information as a per-item category. Drop it (and any
   blank) so those items show no category rather than a meaningless chip. */
function sectionCategory(name: string): string {
  const n = name.trim();
  return n.toLowerCase() === "menu" ? "" : n;
}

function formatPrice(item: ApiExtractedItem): string {
  if (item.price_cents == null) return "—";
  const amount = (item.price_cents / 100).toFixed(2);
  return item.currency && item.currency !== "USD"
    ? `${item.currency} ${amount}`
    : `$${amount}`;
}

export function mapExtractedMenu(res: UploadImageResponse): ExtractedMenu {
  const items: MenuItemRecord[] = [];
  res.menu.sections.forEach((section, si) => {
    section.items.forEach((item, ii) => {
      items.push({
        id: `x${si}-${ii}`, // Provisional — extracted items are not yet persisted.
        name: item.name,
        price: formatPrice(item),
        category: sectionCategory(section.name),
        desc: item.description ?? "",
        sectionId: null, // Not persisted yet, so no backend section id.
      });
    });
  });
  return { items, transcript: res.transcript };
}

/* Extract a menu from one upload — an image or PDF, passed as a data URL.
   `name` gives the model optional restaurant context (e.g. the target
   restaurant's name). */
export async function uploadMenu(
  dataUrl: string,
  name?: string | null,
): Promise<ExtractedMenu> {
  const body: UploadImageRequest = { image: dataUrl, name: name ?? null };
  const res = await gwFetch("/upload_menu", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`Failed to extract menu (${res.status})`);
  }
  const data: UploadImageResponse = await res.json();
  return mapExtractedMenu(data);
}
